import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play, Pause, Square, ChevronDown } from "lucide-react";

const VOICE_PREF_KEY = "dt_tts_voice";
const LADY_HINT = /female|samantha|victoria|karen|zira|susan|allison|jenny|aria|sara|salli|joanna|kimberly|nicole|olivia|emma|amy|serena|moira|tessa|fiona|monica/i;
const MALE_HINT = /male|david|mark|george|daniel|thomas|fred|alex|arthur|guy|ryan|matthew|jorge/i;

// Chunk long text so we don't hit browser TTS length limits (Chrome cuts off around 200-400 chars silently)
function chunkText(text, max = 200) {
  if (!text) return [];
  const clean = text.replace(/\s+/g, " ").trim();
  const parts = clean.match(/[^.!?]+[.!?]+["'"]?\s?|[^.!?]+$/g) || [clean];
  const chunks = [];
  let buf = "";
  for (const p of parts) {
    if ((buf + p).length > max) {
      if (buf) chunks.push(buf.trim());
      if (p.length > max) {
        const sub = p.match(/.{1,200}(?:[,\s]|$)/g) || [p];
        chunks.push(...sub.map(s => s.trim()).filter(Boolean));
        buf = "";
      } else {
        buf = p;
      }
    } else {
      buf += p;
    }
  }
  if (buf.trim()) chunks.push(buf.trim());
  return chunks;
}

// state: "idle" | "playing" | "paused"
export default function TTSPlayer({ text }) {
  const [state, setState] = useState("idle");
  const [available, setAvailable] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voicesTimedOut, setVoicesTimedOut] = useState(false);
  const [preferredVoice, setPreferredVoice] = useState(() => {
    try { return localStorage.getItem(VOICE_PREF_KEY) || ""; } catch { return ""; }
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const queueRef = useRef([]);
  const resumeTickerRef = useRef(null);

  useEffect(() => {
    const has = typeof window !== "undefined" && "speechSynthesis" in window;
    setAvailable(has);
    if (!has) return;
    const load = () => {
      const v = window.speechSynthesis.getVoices();
      if (v && v.length) setVoices(v);
    };
    load();
    window.speechSynthesis.addEventListener("voiceschanged", load);
    const t = setTimeout(() => {
      if (!window.speechSynthesis.getVoices().length) setVoicesTimedOut(true);
    }, 3000);
    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", load);
      clearTimeout(t);
      window.speechSynthesis.cancel();
      if (resumeTickerRef.current) clearInterval(resumeTickerRef.current);
    };
  }, []);

  // Stop any current speech if the source text changes (page navigation)
  useEffect(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      if (resumeTickerRef.current) { clearInterval(resumeTickerRef.current); resumeTickerRef.current = null; }
      queueRef.current = [];
      setState("idle");
    }
  }, [text]);

  const ladyVoices = React.useMemo(() => {
    // English voices, prefer feminine-sounding names, exclude clearly male ones
    const en = voices.filter(v => /^en/i.test(v.lang));
    const scored = en
      .filter(v => !MALE_HINT.test(v.name))
      .map(v => ({ v, score: LADY_HINT.test(v.name) ? 2 : /en-US|en_US/i.test(v.lang) ? 1 : 0 }))
      .sort((a, b) => b.score - a.score);
    const seen = new Set();
    const out = [];
    for (const { v } of scored) {
      const key = `${v.name}|${v.lang}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(v);
    }
    return out;
  }, [voices]);

  const pickFemaleVoice = () => {
    if (!voices.length) return null;
    if (preferredVoice) {
      const match = voices.find(v => v.name === preferredVoice);
      if (match) return match;
    }
    return ladyVoices[0] || voices[0];
  };

  const changeVoice = (name) => {
    setPreferredVoice(name);
    try { localStorage.setItem(VOICE_PREF_KEY, name); } catch {}
    setPickerOpen(false);
    // If currently speaking, cancel — user can hit Play again with the new voice
    if (available && (state === "playing" || state === "paused")) {
      window.speechSynthesis.cancel();
      queueRef.current = [];
      stopTicker();
      setState("idle");
    }
  };

  const startTicker = () => {
    if (resumeTickerRef.current) clearInterval(resumeTickerRef.current);
    resumeTickerRef.current = setInterval(() => {
      if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        try { window.speechSynthesis.pause(); window.speechSynthesis.resume(); } catch (e) {}
      }
    }, 10000);
  };

  const stopTicker = () => {
    if (resumeTickerRef.current) { clearInterval(resumeTickerRef.current); resumeTickerRef.current = null; }
  };

  const speakNext = () => {
    const chunks = queueRef.current;
    if (!chunks.length) {
      setState("idle");
      stopTicker();
      return;
    }
    const chunk = chunks.shift();
    const utter = new SpeechSynthesisUtterance(chunk);
    const voice = pickFemaleVoice();
    if (voice) utter.voice = voice;
    utter.rate = 0.98;
    utter.pitch = 1.05;
    utter.volume = 1;
    utter.onend = () => speakNext();
    utter.onerror = () => speakNext();
    window.speechSynthesis.speak(utter);
  };

  const start = () => {
    if (!available || !text) return;
    window.speechSynthesis.cancel();
    try { window.speechSynthesis.resume(); } catch (e) {}
    queueRef.current = chunkText(text);
    if (!queueRef.current.length) return;
    setState("playing");
    startTicker();
    speakNext();
  };

  const resume = () => {
    if (!available) return;
    try { window.speechSynthesis.resume(); } catch (e) {}
    setState("playing");
    startTicker();
  };

  const pause = () => {
    if (!available) return;
    try { window.speechSynthesis.pause(); } catch (e) {}
    setState("paused");
    stopTicker();
  };

  const stop = () => {
    if (!available) return;
    window.speechSynthesis.cancel();
    queueRef.current = [];
    stopTicker();
    setState("idle");
  };

  if (!available) return null;

  const chosen = pickFemaleVoice();
  const noVoices = voicesTimedOut && voices.length === 0;
  const playing = state === "playing";
  const paused = state === "paused";
  const primaryClick = paused ? resume : start;
  const showLabel = noVoices
    ? "No voice installed"
    : voices.length === 0
      ? "Lady Voice · loading…"
      : paused
        ? "Paused"
        : "Lady Voice";

  return (
    <div data-testid="tts-player" className="sticky bottom-44 md:bottom-4 lg:bottom-6 mx-auto mt-8 max-w-md z-30 pointer-events-none">
      {pickerOpen && (
        <div className="pointer-events-auto mb-2 mx-auto max-w-md rounded-md border border-border bg-card shadow-lg overflow-hidden" data-testid="tts-voice-picker">
          <div className="label-caps px-4 py-2 border-b border-border bg-secondary/50 flex items-center justify-between">
            <span>Pick a Lady Voice</span>
            <button onClick={() => setPickerOpen(false)} className="text-[10px] underline text-muted-foreground" data-testid="tts-voice-picker-close">Close</button>
          </div>
          {ladyVoices.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">Your device didn't ship any Lady Voices. Try a different browser or install a voice pack.</div>
          ) : (
            <ul className="max-h-64 overflow-y-auto py-1">
              {ladyVoices.map(v => (
                <li key={`${v.name}-${v.lang}`}>
                  <button
                    onClick={() => changeVoice(v.name)}
                    data-testid={`tts-voice-option-${v.name.replace(/\s+/g, "-")}`}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-secondary flex items-center justify-between ${chosen?.name === v.name ? "bg-primary/5 text-primary" : ""}`}
                  >
                    <span className="truncate min-w-0">{v.name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0 ml-3">{v.lang}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="pointer-events-auto flex items-center justify-between gap-4 px-5 py-3 rounded-full bg-primary text-primary-foreground shadow-lg border border-primary/40">
        <button
          onClick={() => voices.length && setPickerOpen(o => !o)}
          disabled={!voices.length}
          data-testid="tts-voice-btn"
          className="flex items-center gap-2 min-w-0 rounded-full disabled:cursor-not-allowed"
          title={chosen?.name || ""}
        >
          <div className={`h-2 w-2 rounded-full ${playing ? "bg-accent animate-pulse" : paused ? "bg-accent" : "bg-accent/50"}`} />
          <span className="text-xs tracking-widest uppercase truncate">
            {showLabel}
          </span>
          {voices.length > 0 && <ChevronDown size={12} className="opacity-70 shrink-0" />}
        </button>
        <div className="flex items-center gap-2">
          {!playing ? (
            <Button
              data-testid="tts-play-btn"
              size="sm"
              variant="secondary"
              onClick={primaryClick}
              disabled={noVoices}
              className="h-8 w-8 p-0 rounded-full disabled:opacity-50"
              aria-label={paused ? "Resume" : "Play"}
            >
              <Play size={14} />
            </Button>
          ) : (
            <Button
              data-testid="tts-pause-btn"
              size="sm"
              variant="secondary"
              onClick={pause}
              className="h-8 w-8 p-0 rounded-full"
              aria-label="Pause"
            >
              <Pause size={14} />
            </Button>
          )}
          <Button
            data-testid="tts-stop-btn"
            size="sm"
            variant="secondary"
            onClick={stop}
            disabled={state === "idle"}
            className="h-8 w-8 p-0 rounded-full disabled:opacity-50"
            aria-label="Stop"
          >
            <Square size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
